import { backendServices, BusinessDocType, CreatedFrom, documentUtils, RecursivePartial } from 'common';

/** Mirrors supplier-portal submit (`externalStatus.Processing`). */
const EXTERNAL_STATUS_PROCESSING = 3;

/**
 * Shared helpers for building SIM DocumentViewModel submission bodies.
 *
 * All three post-signup SIM documents (W-9, Remittance, COI) share the
 * same wire shape:
 *   - CommonFields stamped with sender/receiver companies, status, class
 *   - BusinessDocFields.BusinessDocument.Document.BusinessDocument is the
 *     per-class payload (SIMDocument + HeaderInfo fields)
 *
 * Each step converter only has to describe its own subtree under
 * `BusinessDocument`; everything else (merging the skeleton, stamping
 * status, applying the SIM request linkage) lives here so the three
 * converters stay small and parallel.
 */

/** Per-class DocumentClass ids as they appear on the wire. */
export const SIM_DOCUMENT_CLASS = {
    W9: 1,
    InsuranceCertificate: 2,
    Remittance: 4,
} as const;

export type SimDocumentClass = typeof SIM_DOCUMENT_CLASS[keyof typeof SIM_DOCUMENT_CLASS];

/** Document status ids used for SIM draft → submitted transitions. */
export const DOCUMENT_STATUS = {
    Inserted: 1,
    Draft: 34,
} as const;

/** Strip forbidden chars before POST/PUT — mirrors supplier-portal draft save/submit. */
export function sanitizeSimDocumentForApi(
    doc: backendServices.ViewModels.DocumentViewModel
): backendServices.ViewModels.DocumentViewModel {
    return documentUtils.removeForbiddenCharsFromDocument(doc);
}

/** SIM documents always travel Sales -> Procurement. Values mirror common's ProfileType enum. */
export const SIM_PROFILE_TYPE = {
    Procurement: 3,
    Sales: 4,
} as const;

export interface SimSubmissionContext {
    supplierCompanyId: number;
    buyerCompanyId: number;
    supplierCompanyName: string;
    /** Optional: backend backfills ReceiverCompanyName from ReceiverCompanyId if null. */
    buyerCompanyName: string | null;
    /** Required. Supplier's Sales profile id; POST /Document/Draft rejects SIM without it. */
    senderProfileId: number;
    /** Required. Buyer's Procurement profile id; POST /Document/Draft rejects SIM without it. */
    receiverProfileId: number;
    /** TEMPORARY: drop once backend resolves SIM CustomerId server-side. */
    customerId: number | null;
    /** TEMPORARY: same lifecycle as customerId. Stamped into BusinessDocument.CustomerNumber. */
    customerNumber: string | null;
}

/**
 * Minimal shape of the payload the backend expects under
 * `BusinessDocFields.BusinessDocument.Document.BusinessDocument`.
 *
 * We only type the pieces the post-signup forms actually write; the
 * rest is passed through from the fetched skeleton untouched.
 */
export interface SimBusinessDocumentPayload {
    BusinessDocument: {
        CustomerName?: string | null;
        CustomerNumber?: string | null;
        VendorName?: string | null;
        DocumentDate?: string | null;
        SIMRequestId?: number | null;
        SIMDocument: {
            Description?: string | null;
            DocumentClass?: number | null;
            DocumentType?: string | null;
            SIMDocumentTypeId?: number | null;
            HeaderInfo: Record<string, string | number | null | undefined>;
        };
    };
}

/**
 * Inputs to `buildSimDocumentViewModel`. Each step converter calls this
 * once with its own class + payload.
 */
export interface BuildSimDocumentArgs {
    skeleton: RecursivePartial<backendServices.ViewModels.DocumentViewModel>;
    context: SimSubmissionContext;
    documentClass: SimDocumentClass | number;
    /** "W-9", "Remittance", "Certificate of Insurance", etc. - stamped into SIMDocument.Description. */
    description: string;
    /** Short type label stamped into SIMDocument.DocumentType. */
    documentType: string;
    /** Per-step payload under `BusinessDocument.SIMDocument.HeaderInfo`. */
    headerInfo: Record<string, string | number | null | undefined>;
    /** Additional fields merged onto `BusinessDocument.SIMDocument` (e.g. questionnaire Item1, Item2). */
    simDocumentFields?: Record<string, string | number | null | undefined>;
    /** From GET /DocumentType schedule row (`Id`) when the skeleton omits it. */
    simDocumentTypeId?: number | null;
    /** From GET /DocumentType/SupplierSIMRequest row (`Id`). */
    simRequestId?: number;
}

/**
 * Build the final DocumentViewModel to POST to /Document/Draft for a SIM step.
 *
 * Keep this function small and boring: merge skeleton -> set common
 * fields -> set the SIMDocument payload.
 *
 * NOTE(manual testing): to short-circuit submissions while wiring the
 * UI, the orchestrator can swap `submitSimDocument` for a mock that
 * just `console.log`s the body this function produces.
 */
export function buildSimDocumentViewModel({
    skeleton,
    context,
    documentClass,
    description,
    documentType,
    headerInfo,
    simDocumentFields,
    simDocumentTypeId,
    simRequestId,
}: BuildSimDocumentArgs): backendServices.ViewModels.DocumentViewModel {
    const now = new Date().toISOString();

    const baseBusinessDocument: Partial<SimBusinessDocumentPayload['BusinessDocument']> =
        (skeleton.BusinessDocFields?.BusinessDocument?.Document as SimBusinessDocumentPayload | undefined)
            ?.BusinessDocument ?? {};
    const baseSimDocument: Partial<SimBusinessDocumentPayload['BusinessDocument']['SIMDocument']> =
        baseBusinessDocument.SIMDocument ?? {};
    const baseHeaderInfo = baseSimDocument.HeaderInfo ?? {};

    // The server reads this element by XPath and treats "absent" as "no attachment
    // constraint to check", so the key is omitted rather than sent as null.
    const { SIMDocumentTypeId: skeletonSimDocumentTypeId, ...baseSimDocumentRest } = baseSimDocument;
    const resolvedSimDocumentTypeId = simDocumentTypeId ?? skeletonSimDocumentTypeId ?? null;

    const payload: SimBusinessDocumentPayload = {
        BusinessDocument: {
            ...baseBusinessDocument,
            // Inner CustomerName is the buyer (receiver), matching supplier-portal submissions.
            CustomerName: context.buyerCompanyName ?? baseBusinessDocument.CustomerName ?? null,
            CustomerNumber: context.customerNumber ?? baseBusinessDocument.CustomerNumber ?? null,
            VendorName: context.supplierCompanyName,
            DocumentDate: now,
            SIMRequestId: simRequestId ?? null,
            SIMDocument: {
                ...baseSimDocumentRest,
                ...simDocumentFields,
                Description: description,
                DocumentClass: documentClass,
                DocumentType: documentType,
                ...(resolvedSimDocumentTypeId != null ? { SIMDocumentTypeId: resolvedSimDocumentTypeId } : {}),
                HeaderInfo: {
                    ...baseHeaderInfo,
                    ...headerInfo,
                },
            },
        },
    };

    const built = {
        ...(skeleton as backendServices.ViewModels.DocumentViewModel),
        Id: null as unknown as number,
        BusinessDocType: BusinessDocType.SIMDocument,
        BusinessDocType_US: 'SIMDocument',
        CreationDate: skeleton.CreationDate || now,
        LastUpdateDate: now,
        CommonFields: {
            ...(skeleton.CommonFields ?? {}),
            SenderCompanyId: context.supplierCompanyId,
            SenderCompanyName: context.supplierCompanyName,
            SenderProfileId: context.senderProfileId,
            SenderProfileType: SIM_PROFILE_TYPE.Sales,
            ReceiverCompanyId: context.buyerCompanyId,
            ReceiverCompanyName: context.buyerCompanyName ?? null,
            ReceiverProfileId: context.receiverProfileId,
            ReceiverProfileType: SIM_PROFILE_TYPE.Procurement,
            // TEMPORARY: drop once backend resolves SIM CustomerId server-side.
            ...(context.customerId != null ? { CustomerId: context.customerId } : {}),
            CreatedFrom: CreatedFrom.WebEntry,
            BusinessDocType: BusinessDocType.SIMDocument,
            BusinessDocType_US: 'SIMDocument',
            // Envelope DocumentClass is the SIM BusinessDocType (12); sub-class (1/2/4) lives on inner SIMDocument.DocumentClass.
            DocumentClass: BusinessDocType.SIMDocument,
            Status: DOCUMENT_STATUS.Draft,
            Status_US: 'Draft',
            LastUpdateDate: now,
        } as backendServices.ViewModels.DocumentViewModel['CommonFields'],
        BusinessDocFields: {
            ...(skeleton.BusinessDocFields ?? {}),
            BusinessDocument: {
                ...(skeleton.BusinessDocFields?.BusinessDocument ?? {}),
                Document: payload,
                Id: null as unknown as number,
                LastUpdateDate: now,
            },
        } as backendServices.ViewModels.DocumentViewModel['BusinessDocFields'],
    } as backendServices.ViewModels.DocumentViewModel;

    return sanitizeSimDocumentForApi(built);
}

/** PUT body that promotes a draft to submitted; echoes the GET payload so the server-side merge stays clean. */
export function mergeSimDocumentForSubmit(
    existingDoc: backendServices.ViewModels.DocumentViewModel,
    draftId: number
): backendServices.ViewModels.DocumentViewModel {
    const now = new Date().toISOString();
    const bd = existingDoc.BusinessDocFields?.BusinessDocument;

    const merged = {
        ...existingDoc,
        Id: draftId,
        LastUpdateDate: now,
        CommonFields: {
            ...existingDoc.CommonFields,
            Status: DOCUMENT_STATUS.Inserted,
            Status_US: 'Inserted',
            ExternalStatus: EXTERNAL_STATUS_PROCESSING,
            ExternalStatus_US: 'Processing',
            LastUpdateDate: now,
        },
        BusinessDocFields: {
            ...existingDoc.BusinessDocFields,
            ...(bd
                ? {
                      BusinessDocument: {
                          ...bd,
                          LastUpdateDate: now,
                      },
                  }
                : {}),
        },
    } as backendServices.ViewModels.DocumentViewModel;

    return sanitizeSimDocumentForApi(merged);
}
